import React, { useRef, useState } from "react";
import { Card, CardBody, Row, Col, Input, Label, FormGroup } from "reactstrap";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { StepComponentProps } from "../types/proposalForm.types";
import { uploadFileToS3Kyc } from "services/kycService";
import { addLicDocumentAdminThunk, deleteLicDocumentAdminThunk } from "slices/KYC/thunk";

type Step9DocField = "identity_proof" | "address_proof" | "income_proof" | "medical_reports";

const DOC_TYPE_ID_STEP9 = 2 as const;

const DOC_LABELS: Record<Step9DocField, string> = {
  identity_proof: "Identity proof",
  address_proof: "Address proof",
  income_proof: "Income proof",
  medical_reports: "Medical reports",
};

const Step9Submit: React.FC<StepComponentProps> = ({
  values,
  errors,
  touched,
  setFieldValue,
  licId,
}) => {
  const dispatch = useDispatch();
  const [uploadingField, setUploadingField] = useState<Step9DocField | null>(null);
  const identityProofRef = useRef<HTMLInputElement>(null);
  const addressProofRef = useRef<HTMLInputElement>(null);
  const incomeProofRef = useRef<HTMLInputElement>(null);
  const medicalReportsRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: Step9DocField
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File size should not exceed 5MB");
      return;
    }
    const validTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a PNG, JPG, PDF, or DOCX file");
      return;
    }

    if (!licId) {
      toast.error("LIC id is required. Please complete earlier steps first.");
      return;
    }

    setUploadingField(fieldName);
    try {
      const document_url = await uploadFileToS3Kyc(file);
      setFieldValue(fieldName, document_url);

      const doc_typeid = DOC_TYPE_ID_STEP9;
      const result = await dispatch(
        addLicDocumentAdminThunk({
          doc_typeid,
          lic_id: licId,
          document_url,
          document_name: DOC_LABELS[fieldName],
          note: "",
        }) as any
      );
      if (result?.success && result?.document_id != null) {
        const docIdField = `${fieldName}_document_id` as keyof typeof values;
        setFieldValue(docIdField, result.document_id);
        toast.success(`${DOC_LABELS[fieldName]} uploaded.`);
      } else if (!result?.success) {
        toast.error(result?.message ?? "Failed to save document.");
      }
    } catch (err: any) {
      toast.error(err?.message ?? "Upload failed.");
    } finally {
      setUploadingField(null);
      e.target.value = "";
    }
  };

  const renderFileUpload = (
    fieldName: Step9DocField,
    label: string,
    uploadText: string,
    fileRef: React.RefObject<HTMLInputElement>,
    required: boolean = false
  ) => {
    const fileValue = values[fieldName];
    const docIdField = `${fieldName}_document_id` as keyof typeof values;
    const documentId = values[docIdField] as number | null | undefined;
    const hasFile = !!fileValue;

    const handleRemove = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (documentId != null) {
        try {
          const result = await dispatch(deleteLicDocumentAdminThunk(documentId) as any);
          if (!result?.success) {
            toast.error(result?.message ?? "Failed to delete document.");
            return;
          }
          toast.success("Document removed.");
        } catch (err: any) {
          toast.error(err?.message ?? "Failed to delete document.");
          return;
        }
      }
      setFieldValue(fieldName, null);
      setFieldValue(docIdField, null);
      if (fileRef.current) fileRef.current.value = "";
    };

    return (
      <FormGroup className="upload-group">
        <Label className="upload-label">
          {label} {required && <span className="text-danger">*</span>}
        </Label>
        <div
          className={`upload-box ${hasFile ? "has-file" : ""} ${uploadingField === fieldName ? "uploading" : ""}`}
          onClick={() => !uploadingField && fileRef.current?.click()}
        >
          {uploadingField === fieldName ? (
            <p>Uploading...</p>
          ) : hasFile && typeof fileValue === "string" ? (
            <div className="file-preview">
              <a
                href={fileValue}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="document-preview-link"
                onClick={(e) => e.stopPropagation()}
                title="Click to download"
              >
                {/\.(png|jpe?g|webp)(\?|$)/i.test(fileValue) ? (
                  <img src={fileValue} alt="Document preview" className="document-preview-img" />
                ) : (
                  <span className="document-preview-placeholder">
                    <i className="ri-file-text-line" />
                    <span>{/\.pdf(\?|$)/i.test(fileValue) ? "PDF" : /\.docx?(\?|$)/i.test(fileValue) ? "DOCX" : "Document"}</span>
                  </span>
                )}
              </a>
              <button
                type="button"
                className="remove-btn"
                onClick={handleRemove}
                title="Delete document"
                aria-label="Delete document"
              >
                <i className="ri-close-line" aria-hidden />
              </button>
            </div>
          ) : (
            <div className="upload-placeholder">
              <i className="ri-upload-cloud-line" />
              <p>{uploadText}</p>
              <small>PNG, JPG, PDF or DOCX (Max 5MB)</small>
            </div>
          )}
        </div>
        <input
          type="file"
          ref={fileRef}
          hidden
          accept=".png,.jpg,.jpeg,.pdf,.docx"
          onChange={(e) => handleFileChange(e, fieldName)}
        />
        {touched[fieldName] && errors[fieldName] && (
          <div className="error-text">{errors[fieldName]}</div>
        )}
      </FormGroup>
    );
  };

  return (
    <>
      <Card className="form-card">
        <CardBody>
          <h5 className="form-title">Document Upload</h5>
          <p className="form-subtitle">Upload required supporting documents for verification</p>

          <Row>
            <Col md={6}>
              {renderFileUpload("identity_proof", "Identity Proof", "Click to upload Identity copy", identityProofRef, true)}
            </Col>
            <Col md={6}>
              {renderFileUpload("address_proof", "Address Proof", "Click to upload Address proof copy", addressProofRef, true)}
            </Col>
            <Col md={6}>
              {renderFileUpload("income_proof", "Income Proof", "Click to upload Income proof copy", incomeProofRef, true)}
            </Col>
            <Col md={6}>
              {renderFileUpload("medical_reports", "Medical Reports (if applicable)", "Click to upload reports copy", medicalReportsRef)}
            </Col>
          </Row>

          <div className="document-requirements-section">
            <div className="requirements-content">
              <i className="ri-file-list-3-line requirements-icon" />
              <div className="requirements-text">
                <h6 className="requirements-title">Document Requirements</h6>
                <ul className="requirements-list">
                  <li>Document must be valid and not expired.</li>
                  <li>Scans should be clear and all text must be readable.</li>
                  <li>File size should not exceed 5MB per document.</li>
                  <li>Accepted formats: PDF, JPG, PNG, DOCX</li>
                </ul>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card className="form-card mt-4">
        <CardBody>
          <h5 className="form-title">Application Review</h5>

          <div className="ready-to-submit-box">
            <p className="ready-to-submit-text">Ready to Submit</p>
            <p className="ready-to-submit-instruction">
              Please review all the information above. Once submitted, you will
              receive a confirmation email with your proposal reference number.
            </p>
          </div>

          <div className="review-confirmation-checkbox" style={{ marginTop: "1.5rem" }}>
            <FormGroup check className="form-check">
              <Input
                type="checkbox"
                id="review_confirmation"
                name="review_confirmation"
                checked={values.review_confirmation || false}
                onChange={(e) => setFieldValue("review_confirmation", e.target.checked)}
                className="form-check-input"
              />
              <Label check htmlFor="review_confirmation" className="form-check-label">
                I confirm that I have reviewed all the information provided in this application. I understand that once I submit the form, I will not be able to edit the information.
              </Label>
            </FormGroup>
            {touched.review_confirmation && errors.review_confirmation && (
              <div className="invalid-feedback">{errors.review_confirmation}</div>
            )}
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default Step9Submit;
